// HistoryVoucher.tsx
import { useMemo, useState } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Chip, Paper } from "@mui/material";
import { HistoryVoucherType } from "@/types";
import { formatDDMMYYYY, formatDiscount } from "@/utilities";
import { HistoryVoucherProps } from "./HistoryVoucher";

type VoucherType = "discount" | "gift_card" | "free_product" | "bogo";

const LABELS: Record<VoucherType, string> = {
  discount: "Réduction",
  gift_card: "Bon d'achat",
  free_product: "Produit offert",
  bogo: "BOGO",
};

export default function HistoryVoucherWithEntities({
  data,
  height = 560,
}: HistoryVoucherProps) {
  const rows = useMemo(
    () =>
      data.map((r, i) => ({
        id: `${r.user_id}-${r.created_at}-${r.type}-${i}`,
        ...r,
      })),
    [data]
  );

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const columns = useMemo<GridColDef<HistoryVoucherType>[]>(
    () => [
      { field: "entityname", headerName: "Entité", flex: 1 },
      { field: "reference", headerName: "Référence", flex: 1 },
      {
        field: "voucher_type",
        headerName: "Type de bon",
        flex: 1,
        renderCell: ({ value }) => {
          const label = LABELS[value as VoucherType] ?? value;
          return (
            <Chip
              size="small"
              label={label}
              sx={(t) => {
                const bg =
                  value === "discount"
                    ? t.palette.secondary.main
                    : value === "gift_card"
                    ? t.palette.primary.main
                    : value === "free_product"
                    ? "#F2C239"
                    : "#4339F2";
                return { backgroundColor: bg, color: "#fff", fontWeight: 500 };
              }}
            />
          );
        },
      },
      {
        field: "discount_value",
        headerName: "Avantage",
        flex: 1,
        valueGetter: (_value, row) => {
          return formatDiscount(row.discount_value, row.discount_type);
        },
      },
      {
        field: "invoice_amount",
        headerName: "Montant facture",
        flex: 1,
        valueGetter: (_value, row) => {
          return formatDiscount(row.invoice_amount, row.discount_type);
        },
      },
      {
        field: "type",
        headerName: "Statut",
        flex: 1,
        sortable: true,
        renderCell: ({ value }) => {
          const color = value === "consumed" ? "success" : "default";
          const label = value === "consumed" ? "Réclamé" : "Consomé";
          return <Chip size="small" color={color} label={label} />;
        },
      },
      {
        field: "created_at",
        headerName: "Date",
        flex: 1,
        valueGetter: (v) => (typeof v === "string" ? formatDDMMYYYY(v) : ""),
        sortComparator: (a, b) => {
          const da = new Date(a).getTime();
          const db = new Date(b).getTime();
          return da - db;
        },
      },
    ],
    []
  );

  return (
    <Paper sx={{ height, width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        initialState={{
          pagination: { paginationModel },
          sorting: { sortModel: [{ field: "created_at", sort: "desc" }] },
        }}
        pageSizeOptions={[5, 10, 25, 50]}
        checkboxSelection={false}
        disableRowSelectionOnClick
        sx={{ border: 0 }}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
      />
    </Paper>
  );
}
